import "./grouped-multi-select.scss";

import { BindingEngine, bindingMode, computedFrom, Disposable, observable } from "aurelia-framework";
import { bindable, customElement } from "aurelia-templating";
import * as $ from "jquery";

import nameof from "../../../common/nameof";
import { EnumMap } from "../../../common/utilities/enum-map";
import { IMultiSelectDropDownOption } from "../../../interfaces/i-grouped-multi-select";
import { bind } from "lodash";
import { arrayOverride } from "../../../common/utilities/object-extensions";

@customElement("grouped-multi-select")
export class GroupedMultiSelect {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public singularLabelText: string = "";
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public placeholderText: string = "";
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public searchText: string = "";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public checkAllByDefault: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public groupNameEnumMap: EnumMap;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public optionEnumMap: EnumMap;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public labelText: string = "";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isDisabled: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public defaultBranchName: boolean;
    @bindable({ defaultBindingMode: bindingMode.toView })
    @observable({
        changeHandler: nameof<GroupedMultiSelect>("dropDownOptionsChanged")
    })
    public dropDownOptions: IMultiSelectDropDownOption;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedOptions: any[] = [];
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showSelectAll: boolean = true;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public maxSelectedLimit: number = 0;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public noneSelectedMeansAll: boolean = false;
    @observable({ changeHandler: "searchChanged" })
    public search: string;
    @observable({
        changeHandler: nameof<GroupedMultiSelect>("displaySelectedOptionsChanged")
    })
    // If emptyWhenAllSelected = true, then this will retain all the selections on SelectAll = true, for display purposes. otherwise it is the reference to selectedOptions
    public displaySelectedOptions: any[];
    private _bindingEngine: BindingEngine;
    private _selectedSubscription: Disposable;
    public filteredOptions: IMultiSelectDropDownOption;
    public dropDownMenu: HTMLElement;
    public dropDownElement: HTMLElement;
    public searchInput: HTMLElement;

    @computedFrom(nameof<GroupedMultiSelect>("labelText"))
    public get searchPlaceholder(): string {
        return this.searchText ? `Search for ${this.searchText}` : `Search for ${this.labelText}`;
    }

    @computedFrom(nameof<GroupedMultiSelect>("dropDownOptions"))
    public get dropDownOptionsLength() {
        if (!this.dropDownOptions) {
            return 0;
        }
        return Object.values(this.dropDownOptions).reduce((len, option) => option.length + len, 0);
    }

    @computedFrom(
        `${nameof<GroupedMultiSelect>("displaySelectedOptions")}.length`,
        nameof<GroupedMultiSelect>("dropDownOptionsLength")
    )
    public get buttonText(): string {
        if (this.hasAllSelected) {
            if (this.defaultBranchName) {
                return `${this.labelText}: All`;
            } else {
                return `All ${this.labelText}`;
            }
        } else if (this.displaySelectedOptions?.length === 0) {
            return this.placeholderText;
        } else if (this.displaySelectedOptions?.length === 1) {
            return `1 ${this.singularLabelText ? this.singularLabelText : this.labelText} selected`;
        } else {
            return `${this.displaySelectedOptions?.length} ${this.labelText} selected`;
        }
    }

    @computedFrom(
        nameof<GroupedMultiSelect>("dropDownOptions"),
        `${nameof<GroupedMultiSelect>("selectedOptions")}.length`,
        nameof<GroupedMultiSelect>("placeholderText")
    )
    public get hasAllSelected() {
        return (
            this.displaySelectedOptions?.length === this.dropDownOptionsLength ||
            (this.displaySelectedOptions?.length === 0 && this.noneSelectedMeansAll)
        );
    }

    public constructor(bindingEngine: BindingEngine) {
        this._bindingEngine = bindingEngine;
    }

    public attached() {
        this.filteredOptions = { ...this.dropDownOptions };
        $(this.dropDownMenu).on("click", (e) => {
            e.stopPropagation();
        });
        $(this.dropDownElement).on("shown.bs.dropdown", (e) => {
            this.searchInput.focus();
        });

        if (!this.noneSelectedMeansAll) {
            this.displaySelectedOptions = this.selectedOptions;
        } else {
            this.displaySelectedOptions = [...this.selectedOptions];
        }

        if (this.checkAllByDefault) {
            this.toggleAllSelected();
        }

        this._selectedSubscription = this._bindingEngine
            .collectionObserver(this.displaySelectedOptions)
            .subscribe(() => this.displaySelectedOptionsChanged());
    }

    public toggleAllSelected() {
        let shouldAddAll = this.dropDownOptionsLength !== this.displaySelectedOptions?.length;
        // emptying array
        this.displaySelectedOptions?.splice(0, this.displaySelectedOptions?.length);
        if (shouldAddAll) {
            for (let groupName of Object.keys(this.dropDownOptions)) {
                this.selectAllGroupOptions(groupName);
            }
            arrayOverride(this.selectedOptions, this.displaySelectedOptions);
        } else {
            arrayOverride(this.selectedOptions, []);
        }
    }

    public isLimitReached(index: number): boolean {
        return (
            this.maxSelectedLimit !== 0 &&
            this.displaySelectedOptions.length >= this.maxSelectedLimit &&
            !(this.displaySelectedOptions.indexOf(index) > -1)
        );
    }

    public closeDropDown() {
        $(this.dropDownElement).dropdown("toggle");
    }

    public searchChanged() {
        if (!!this.search) {
            for (let groupName of Object.keys(this.dropDownOptions)) {
                let options: any = this.dropDownOptions[groupName];
                this.filteredOptions[groupName] = options.filter((option: string | number) => {
                    let displayText = this.optionEnumMap.getEnumName(option) as string;
                    return displayText.toLowerCase().includes(this.search.toLowerCase());
                });
            }
        } else {
            this.filteredOptions = { ...this.dropDownOptions };
        }
    }

    public toggleAllGroupOptions(groupName: string) {
        let options = this.dropDownOptions[groupName];
        let shouldSelectAll = false;
        for (let option of options) {
            let optionIndex = this.displaySelectedOptions.indexOf(option);
            if (
                optionIndex === -1 &&
                (this.maxSelectedLimit === 0 ||
                    (this.maxSelectedLimit !== 0 &&
                        this.displaySelectedOptions.length + this.dropDownOptions[groupName].length <=
                            this.maxSelectedLimit))
            ) {
                shouldSelectAll = true;
            } else {
                this.displaySelectedOptions.splice(optionIndex, 1);
            }
        }
        if (shouldSelectAll) {
            this.selectAllGroupOptions(groupName);
        }
    }

    public dropDownOptionsChanged() {
        this.filteredOptions = { ...this.dropDownOptions };
    }

    private selectAllGroupOptions(groupName: string) {
        for (let option of this.dropDownOptions[groupName]) {
            this.displaySelectedOptions.push(option);
        }
    }

    public displaySelectedOptionsChanged() {
        if (!this.noneSelectedMeansAll || (this.hasAllSelected && this.selectedOptions.length === 0)) {
            return;
        }

        if (this.displaySelectedOptions.length === this.dropDownOptionsLength) {
            this.selectedOptions.splice(0, this.selectedOptions.length);
        } else {
            arrayOverride(this.selectedOptions, this.displaySelectedOptions);
        }
    }

    public clearSelection() {
        this.displaySelectedOptions.splice(0, this.displaySelectedOptions.length);
    }

    public detached() {
        this._selectedSubscription?.dispose();
    }
}
